// BB.1.1 Choose an Account
import moment from 'moment';
import { ServerTable, Event as vuetables2Event } from 'vue-tables-2';
import scrollUp from '../shared/vue/mixins/scrollUp';
import { select, each } from '../shared/utils/dom';
import { api, linkedAccountsApi } from './api';

function formatCurrency(amount) {
  // add currency puncuation as needed
  const formatted = amount.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
  return amount < 0 ? `Credit of $${formatted}` : `$${formatted}`;
}

export default function chooseAccount() {
  const el = select('[data-vue-app="choose-an-account"]');
  if (!el) {
    return;
  }

  // @see https://github.com/matfish2/vue-tables-2

  // start data
  const data = {};

  // the url
  data['url'] = `/LinkedAccounts/1.0`;

  // columns
  data['columns'] = ['accountNickname', 'accountStatus', 'dueDate', 'currentCharges', 'paymentStatus', 'paymentAmount'];

  // has loaded data for the first time?
  data['hasMounted'] = false;

  // if the table is loading data from the server
  data['loading'] = false;

  data['enabledLdcs'] = null;

  data['starredRow'] = null;

  data['selectedRow'] = null;

  data['starredAccountIsNotActive'] = false;

  // start options
  const options = {
    dateColumns: ['dueDate'],
    dateFormat: 'MMM D, YYYY',
  };

  // filterable?
  options['filterable'] = false;

  // Disable sorting (currently not supported on server)
  options['sortable'] = [];

  options['headings'] = {
    accountNickname: 'Account Nickname',
    accountStatus: 'Account Status',
    dueDate: 'Due Date',
    currentCharges: 'Current Charges Due',
    paymentStatus: 'Payment Status',
    paymentAmount: 'Payment Amount',
  };

  options['rowClassCallback'] = function (row) {
    return `row-account row-${row.accountId}`;
  };

  function camelCaseToSpaces(str) {
    if (str) {
      return str.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
    return null;
  }

  function checkLdcDeployStatus(ldc) {
    if (app.enabledLdcs.indexOf(ldc) === -1) { // eslint-disable-line no-use-before-define
      return false;
    }
    return true;
  }

  function updateStarredAccountIsNotActive(starredAccountIsNotActive) {
    app.starredAccountIsNotActive = starredAccountIsNotActive; // eslint-disable-line no-use-before-define
  }

  function updateStarredRow(row) {
    app.starredRow = row; // eslint-disable-line no-use-before-define
  }

  /**
   * Request Function to custom handle the Request
   * @param {object} _data
   */
  options.requestFunction = function (params) {
    return api.get(this.url, params)
      .catch(function (e) { // eslint-disable-line prefer-arrow-callback
        this.loading = false;
        this.dispatch('error', e);
      }.bind(this));
  };

  /**
   * Adapt the response
   * @param {response} object
   */
  options.responseAdapter = function (response) { // eslint-disable-line object-shorthand
    if (typeof response === "undefined") {
      return;
    }

    const localData = [];
    const count = response.data.count;

    each(response.data.linkedAccounts, ({ customerAccountId, linkedAccountNickname, nextPayment, customerAccountBalance, ldc, status, starred, arrangementTypeIsValidPlan, budgetEnrollment, serviceAddressCount, serviceAddress }) => {
      if (starred && status !== 'Active') {
        updateStarredAccountIsNotActive(true);
      }
      const newRow = {
        accountNickname: linkedAccountNickname,
        accountStatus: checkLdcDeployStatus(ldc) ? status : 'Manage in Legacy System',
        dueDate: customerAccountBalance && customerAccountBalance.dueDate && checkLdcDeployStatus(ldc) ? moment(customerAccountBalance.dueDate).format('MMM D, YYYY') : null,
        currentCharges: customerAccountBalance ? customerAccountBalance.currentAmountDue : 0,
        paymentStatus: nextPayment && checkLdcDeployStatus(ldc) ? nextPayment.paymentType : '',
        paymentAmount: nextPayment ? nextPayment.paymentAmount : 0,
        paymentDate: nextPayment && nextPayment.paymentDateTime ? moment(nextPayment.paymentDateTime).format('MMM D, YYYY') : '',
        // need the account number to display with nickname
        // this will not display since key not defined in columns
        accountId: customerAccountId,
        ldc, // es6 allows us to not need the `ldc: ldc`
        starred,
        // get checkpayment info for various data within
        checkType: nextPayment && nextPayment.checkPayment ? nextPayment.checkPayment.checkType : '',
        accountNumber: nextPayment && nextPayment.checkPayment ? nextPayment.checkPayment.accountNumber : '',
        pastDueAmount: customerAccountBalance && customerAccountBalance.pastDueAmount ? customerAccountBalance.pastDueAmount : '',
        arrangementTypeIsValidPlan,
        budgetEnrollment,
        budgetArrears: customerAccountBalance && customerAccountBalance.budgetArrears ? customerAccountBalance.budgetArrears : 0,
        avoidShutoffAmount: customerAccountBalance && customerAccountBalance.avoidShutoffAmount ? customerAccountBalance.avoidShutoffAmount : 0,
        // bundle deployed status into here? or no?
        serviceAddressCount,
        serviceAddressToDisplay: serviceAddressCount === 1 ? `${serviceAddress != null && serviceAddress.address1 != null ? serviceAddress.address1 : ''} ${serviceAddress != null && serviceAddress.address2 != null ? serviceAddress.address2 : ''}` : ``,
      };
      if (starred) {
        updateStarredRow(newRow);
      }
      localData.push(newRow);
    });

    if (response.config.params.page === 1) {
      if (localData.length > 0 && localData[0].accountStatus !== 'Active') {
        updateStarredAccountIsNotActive(true);
      }
    }
    return { // eslint-disable-line consistent-return
      data: localData,
      count,
    };
  };

  // how many results per page?
  options['perPage'] = 10;

  // start templates
  const templates = {};

  /**
   * Template construction for "Account Nickname"
   */
  templates.accountNickname = function (h, row) {
    /* eslint-disable */
    return (
      <div class='table-cell-wrapper table-cell-header'>
        <div class="table-cell-label">Account Nickname</div>
        <div class={`table-cell-content account-nickname ${row.starred ? 'favorite' : ''}`}>
          {starIcon(h, row)}
          <div>
            <div><strong>{row.accountNickname}</strong>           
            <a style="display:inline;color:#0072CE;" href={`/account/manage/${row.accountId}?fromurl=ViewAccount`}>            
            <svg style="margin-right: 0px;margin-left: 0px;padding-right :0px; width: 24px;height: 30px;background-color: transparent;" className="icon icon-penciledit">
              <use xlinkHref="#icon-penciledit"></use>
            </svg>
            Edit
            </a>
            </div>
            <div v-if="row.serviceAddressCount === 1">{row.serviceAddressToDisplay}</div>
            <span class="text-gray">#{row.accountId}</span>
          </div>
        </div>
      </div>
    );
    /* eslint-enable */
  };

  /**
   * Function to show different star icons
   */
  function starIcon(h, row) {
    if (row.starred === true) {
      return (
        <svg className="icon icon-star starred" on-click={ () => app.toggleFavorite(event, row) // eslint-disable-line no-use-before-define
        }>
          <use xlinkHref="#icon-star"></use>
        </svg>
      );
    }
    return (
      <svg className="icon icon-star" on-click={ () => app.toggleFavorite(event, row) // eslint-disable-line no-use-before-define
      }>
        <use xlinkHref="#icon-star"></use>
      </svg>
    );
  }

  /**
   * Template for Account Status
   */
  templates.accountStatus = function (h, row) {
    return (
      <div class="table-cell-wrapper">
        <div class="table-cell-label">Account Status</div>
        <div class="table-cell-content">{camelCaseToSpaces(row.accountStatus)}</div>
      </div>
    );
  }

  /**
   * Function to construct the account nickname / link
   */
  function dueDateFormat(h, row) {
    if (checkLdcDeployStatus(row.ldc)) {
      if ((row.pastDueAmount > 0 && row.arrangementTypeIsValidPlan === false && row.budgetEnrollment === false)
          || (row.avoidShutoffAmount > 0 && row.arrangementTypeIsValidPlan === false) // term is always immediately
          || (row.budgetEnrollment === true && row.budgetArrears > 0)
      ) {
        return (<span class="text-red text-sm">Immediately</span>);
      }
      return (<span class="text-sm">{row.dueDate}</span>);
    }
    return (<span class="text-sm"></span>);
  }

  /**
   * Template for Due Date
   */
  templates.dueDate = function (h, row) {
    return (
      <div class="table-cell-wrapper">
        <div class="table-cell-label">Due Date</div>
        <div class="table-cell-content">{dueDateFormat(h, row)}</div>
      </div>
    );
  }

  /**
   * Template for Balance
   */
  templates.currentCharges = function (h, row) {
    if (checkLdcDeployStatus(row.ldc)) {
      return (
        <div class="table-cell-wrapper">
          <div class="table-cell-label">Current Charges Due</div>
          <div class="table-cell-content"><span class="text-sm">{formatCurrency(row.currentCharges)}</span></div>
        </div>
      );
    }
    return (
      <div class="table-cell-wrapper">
        <div class="table-cell-label"></div>
        <div class="table-cell-content"><span class="text-sm"></span></div>
      </div>
    );
  }

  /**
   * function to mask the check number
   */
  function checkAccountMaskAllButLast4Chars(h, row) {
    const accountNum = `${row.accountNumber}`;
    if (accountNum.length > 4) {
      return (`XXXX ${accountNum.substring(accountNum.length - 4)}`);
    }
    return (`XXXX ${row.accountNumber}`);
  }

  /**
   * function to check the check type
   */
  function checkTypeFormat(h, row) {
    if (row.checkType && row.checkType === 'C') {
      return (`Checking`);
    } else if (row.checkType && row.checkType === 'S') {
      return (`Savings`);
    }
    return ('');
  }

  /**
   * function for different payment status states
   */
  function paymentStatusStates(h, row) {
    /* eslint-disable */
    if (checkLdcDeployStatus(row.ldc)) {
      if (row.paymentStatus) {
        return (
          <div>
            <svg className="icon icon-autopay">
              <use xlinkHref="#icon-autopay"></use>
            </svg>
            <strong>Scheduled</strong>
            <span>For {row.paymentDate}</span>
            <span>{checkTypeFormat(h, row)} {checkAccountMaskAllButLast4Chars(h, row)}</span>
          </div>
        );
      } else if ((row.pastDueAmount > 0 && row.arrangementTypeIsValidPlan===false && row.budgetEnrollment===false)
                    || (row.avoidShutoffAmount > 0 && row.arrangementTypeIsValidPlan === false) //term is always past due.
                    || (row.budgetEnrollment === true && row.budgetArrears > 0)) {
        return (
          <div>
            <svg className="icon icon-warning">
              <use xlinkHref="#icon-warning"></use>
            </svg>
            <strong>Past Due</strong>
            <span>Balance is overdue</span>
          </div>
        )
      }
      return ('');
    }
    return ('');
    /* eslint-enable */
  }

  /**
   * Template for Payment Status
   */
  templates.paymentStatus = function (h, row) {
    return (
      <div class="table-cell-wrapper">
        <div class="table-cell-label">Payment Status</div>
        <div class="table-cell-content payment-status">
          {paymentStatusStates(h, row)}
        </div>
      </div>
    );
  }

  /**
   * Template for Payment Amount
   */
  templates.paymentAmount = function (h, row) {
    if (checkLdcDeployStatus(row.ldc)) {
      return (
        <div class="table-cell-wrapper">
          <div class="table-cell-label">Payment Amount</div>
          <div class="table-cell-content"><span class="text-sm">{formatCurrency(row.paymentAmount)}</span></div>
        </div>
      );
    }
    return (
      <div class="table-cell-wrapper">
        <div class="table-cell-label"></div>
        <div class="table-cell-content"><span class="text-sm"></span></div>
      </div>
    );
  }

  // set templates into options
  options['templates'] = templates;

  // set options into data
  data['options'] = options;

  Vue.use(ServerTable);

  const app = new Vue({
    el,
    mixins: [scrollUp],
    data() {
      return data;
    },
    mounted() {
      // once app is mounted, check for deployed LDCs
      if (this.$el.dataset.enabledLdcs) {
        this.enabledLdcs = this.$el.dataset.enabledLdcs;
      }
      if (this.$el.dataset.starredAccountIsNotActive) {
        this.starredAccountIsNotActive = JSON.parse(this.$el.dataset.starredAccountIsNotActive);
      }
    },
    created() {
      Event.$on('A11yDialogConfirm:toggle-favorite', () => {
        this.updateAccountFavorite();
      });
      Event.$on('A11yDialogConfirm:toggle-un-favorite', () => {
        this.updateAccountFavorite();
      });
    },
    methods: {
      toggleFavorite(event, row) {
        event.stopPropagation();
        this.selectedRow = row;
        if (row.starred) {
          Event.$emit('A11yDialogOpen', { dialogKey: 'toggle-un-favorite' });
        } else {
          Event.$emit('A11yDialogOpen', { dialogKey: 'toggle-favorite' });
        }
      },
      updateAccountFavorite() {
        const row = this.selectedRow;
        this.loading = true;
        const requestData = {
          accountNickname: row.accountNickname,
          starred: !row.starred,
        };
        linkedAccountsApi.updateAccountProfile(row.accountId, requestData)
          .then(() => {
            this.loading = false;
            if (this.starredRow != null) {
              this.starredRow.starred = false;
            }
            if (this.starredRow == null || this.starredRow.accountId !== row.accountId) {
              row.starred = true;
              this.starredRow = row;
            } else {
              this.starredRow = null;
            }
            linkedAccountsApi.updateFavoriteAccount(row.accountId, row.accountStatus, row.starred)
              .then(() => {
                if (!row.starred) {
                  this.starredAccountIsNotActive = false;
                } else if (row.accountStatus === 'Active') {
                  this.starredAccountIsNotActive = false;
                } else {
                  this.starredAccountIsNotActive = true;
                }
              });
          })
          .catch((err) => {
            console.log(err);
            this.loading = false;
            this.scrollUp();
          });
      },
    },
  });

  // this will remove the initial loading state and show the table
  vuetables2Event.$on('vue-tables.loaded', () => {
    app.hasMounted = true;
    app.loading = false;
  });

  // this will remove the initial loading state and show the table
  vuetables2Event.$on('vue-tables.loading', () => {
    app.loading = true;
  });

  // this will remove the initial loading state and show the table
  vuetables2Event.$on('vue-tables.error', () => {
    app.hasMounted = true;
    app.loading = false;
  });

  vuetables2Event.$on('vue-tables.row-click', (row) => {
    if (row.row.accountStatus && row.row.accountStatus !== 'NotAvailable') {
      if (row.row.accountStatus === 'Manage in Legacy System') {
        window.location.href = `/wss/${row.row.ldc}`;
      } else {
        window.location.href = `/dashboard/${row.row.accountId}/${row.row.ldc}?allowAccountToLoad=true`;
      }
    }
  });
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/chooseAccount.js